import React, { useState, useRef } from "react";
import axios from "axios";
import { HotTable } from "@handsontable/react";
import { registerAllModules } from "handsontable/registry";
import Handsontable from "handsontable";
import "handsontable/dist/handsontable.full.min.css";
import * as XLSX from 'xlsx';
import './Tracking.css';

registerAllModules();

const Tracking = ({ onBack, authEmail, apiBaseUrl, findFirstScan, isDelivered }) => {
    // Use the reliable webhook for production tracking as requested by user
    const trackApiUrl = process.env.NODE_ENV === 'development'
        ? `${apiBaseUrl}/track`
        : 'https://webhook-iia66khndq-uc.a.run.app/track';

    const [trackingNumbers, setTrackingNumbers] = useState("");
    const [trackingData, setTrackingData] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [searchTime, setSearchTime] = useState(null);
    const hotRef = useRef(null);
    const abortControllerRef = useRef(null);

    const downloadExcel = () => {
        if (trackingData.length === 0) {
            setError("No data available to download.");
            return;
        }

        const worksheetData = trackingData.map(tracking => {
            const referenceArray = tracking.data?.referenceNumber || [];
            const ref1 = referenceArray[0]?.number || "N/A";
            const ref2 = referenceArray[1]?.number || "N/A";
            const ref3 = referenceArray[2]?.number || "N/A";
            const ref4 = referenceArray[3]?.number || "N/A";
            const ref5 = referenceArray[4]?.number || "N/A";

            const alternateTrackingJoined = tracking.data?.alternateTrackingNumber
                ? tracking.data.alternateTrackingNumber.map(a => a.number).join(', ')
                : "N/A";

            const lastAct = tracking.data?.activity?.[0];
            const lastScanDesc = lastAct?.status?.statusCode === "011"
                ? "Delivered"
                : (lastAct?.status?.description?.trim() || "No recent activity");

            const currentStatusObj = tracking.data?.currentStatus;
            const currentStatusCode = currentStatusObj?.statusCode || currentStatusObj?.status?.statusCode;
            const currentStatusText = currentStatusCode === "011"
                ? "Delivered"
                : (currentStatusObj?.description?.trim() || (currentStatusObj?.status?.description?.trim()) || "N/A");

            const firstArrival = typeof findFirstScan === 'function' ? findFirstScan(tracking.data?.activity) : null;
            const firstArrivalText = firstArrival?.text || "No arrival scan";

            return {
                "First Arrival": firstArrivalText,
                "Tracking Number": tracking.number,
                "ICIRS Number": tracking.data?.referenceNumber?.[1]?.number?.slice(0, 6) || "N/A",
                "Reference 1": ref1,
                "Reference 2": ref2,
                "Reference 3": ref3,
                "Reference 4": ref4,
                "Reference 5": ref5,
                "Alternate Tracking Numbers": alternateTrackingJoined,
                "Status": currentStatusText,
                "Delivery Date": tracking.data?.deliveryDate?.[0]?.date || "N/A",
                "Last Scan": lastScanDesc,
                "Last Scan Country": lastAct?.location?.address?.countryCode || "No recent activity",
                "Time": lastAct?.time || "N/A",
                "Date": lastAct?.date || "N/A",
                "Signed By": tracking.data?.deliveryInformation?.receivedBy || "N/A",
                "Destination Country": tracking.data?.packageAddress?.[1]?.address?.countryCode || "N/A",
                "Destination City": tracking.data?.packageAddress?.[1]?.address?.city || "N/A",
                "Slic": lastAct?.location?.slic || "N/A",
                "Delivery Type": tracking.data?.deliveryInformation?.location || "N/A",
                "Service": tracking.data?.service?.description || "N/A",
                "Label Actual Weight": tracking.data?.weight?.weight || "N/A",
                "Origin Country": tracking.data?.packageAddress?.[0]?.address?.countryCode || "N/A",
                "Origin City": tracking.data?.packageAddress?.[0]?.address?.city || "N/A",
                "Package Count": tracking.data?.packageCount || "N/A",
                "Shipment Number": tracking.data?.referenceNumber?.[1]?.number || "N/A",
                "Width": tracking.data?.dimension?.width || "N/A",
                "Height": tracking.data?.dimension?.height || "N/A",
                "Length": tracking.data?.dimension?.length || "N/A",
                "Dimensional Weight": tracking.data?.dimension ? (() => {
                    const dimWeight = (tracking.data.dimension.length * tracking.data.dimension.width * tracking.data.dimension.height) / 5000;
                    return dimWeight < 20 ? (Math.round(dimWeight * 2) / 2).toFixed(1) : Math.round(dimWeight).toFixed(0);
                })() : "N/A"
            };
        });

        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Tracking Data");
        XLSX.writeFile(workbook, "TrackingData.xlsx");
    };

    const handleTrack = async () => {
        setError(null);
        setLoading(true);
        setTrackingData([]);
        setProgress(0);
        setSearchTime(null);

        const numbersArray = trackingNumbers.split(/\n|,/).map(num => num.trim()).filter(num => num !== "");

        if (numbersArray.length === 0) {
            setError("Please enter at least one tracking number.");
            setLoading(false);
            return;
        }

        const startTime = Date.now();
        const totalNumbers = numbersArray.length;
        abortControllerRef.current = new AbortController();

        try {
            for (let i = 0; i < totalNumbers; i++) {
                try {
                    // Use the unified tracking URL (local or webhook)
                    const response = await axios.get(`${trackApiUrl}/${numbersArray[i]}`, {
                        headers: { 'Access-Control-Allow-Origin': '*', 'Content-Type': 'application/json' },
                        signal: abortControllerRef.current.signal,
                    });
                    const packageData = response.data.trackResponse?.shipment[0]?.package[0];
                    const result = { number: numbersArray[i], data: packageData || null };
                    setTrackingData(prev => [...prev, result]);
                } catch (error) {
                    if (axios.isCancel(error)) break;
                    const result = { number: numbersArray[i], data: null };
                    setTrackingData(prev => [...prev, result]);
                }
                setProgress(((i + 1) / totalNumbers) * 100);
                await new Promise(resolve => setTimeout(resolve, 1));
            }
        } catch (error) {
            setError("An error occurred while fetching tracking data.");
        } finally {
            const endTime = Date.now();
            setSearchTime(((endTime - startTime) / 1000).toFixed(0));
            setLoading(false);
        }
    };

    const handleStop = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            setLoading(false);
        }
    };

    const handleViewDetails = (trackingNumber) => {
        window.open(`/shipment-details?trackingNumber=${trackingNumber}`);
    };

    const buttonRenderer = (hotInstance, td, row, col, prop, value, cellProperties) => {
        Handsontable.dom.empty(td);
        const button = document.createElement('button');
        button.innerText = "View Details";
        button.className = 'view-details-btn';

        const trackingEntry = trackingData[row];
        if (isDelivered(trackingEntry)) {
            button.classList.add('delivered');
        }

        button.onclick = () => handleViewDetails(trackingData[row].number);
        button.onmousedown = (e) => e.stopPropagation();
        td.appendChild(button);
        return td;
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            const content = e.target.result;
            let numbersArray = new Set();

            if (file.type === "text/plain") {
                const lines = content.split(/\r?\n/);
                lines.forEach(line => {
                    const matches = line.match(/(1Z[A-Z0-9]{16})/g);
                    if (matches) matches.forEach(match => numbersArray.add(match));
                });
            } else {
                const workbook = XLSX.read(content, { type: 'binary' });
                const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                const sheetData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
                sheetData.flat()
                    .map(num => num.toString().trim())
                    .filter(num => /^1Z[A-Z0-9]{16}$/.test(num))
                    .forEach(validNum => numbersArray.add(validNum));
            }

            if (numbersArray.size > 0) {
                setTrackingNumbers(Array.from(numbersArray).join('\n'));
                setError("");
            } else {
                setError("No valid tracking numbers found.");
            }
        };

        if (file.type === "text/plain") reader.readAsText(file);
        else reader.readAsBinaryString(file);
    };

    const hotHeaders = [
        "View Details", "Tracking Number", "ICIRS Number", "First Arrival", "Last Scan", "Delivery Date", "Status",
        "Last Scan Country", "Time", "Date", "Signed By", "Destination Country", "Destination City", "Slic",
        "Delivery Type", "Service", "Label Actual Weight", "Origin Country", "Origin City", "Package Count",
        "Shipment Number", "Width", "Height", "Length", "Reference 1", "Reference 2", "Reference 3", "Reference 4",
        "Reference 5", "Alternate Tracking Numbers", "Dimensional Weight"
    ];

    const hotData = trackingData.map(tracking => {
        const height = tracking.data?.dimension?.height || 0;
        const length = tracking.data?.dimension?.length || 0;
        const width = tracking.data?.dimension?.width || 0;
        const dimWeight = (length * width * height) / 5000;
        const referenceArray = tracking.data?.referenceNumber || [];
        const ref1 = referenceArray[0]?.number || "N/A";
        const ref2 = referenceArray[1]?.number || "N/A";
        const ref3 = referenceArray[2]?.number || "N/A";
        const ref4 = referenceArray[3]?.number || "N/A";
        const ref5 = referenceArray[4]?.number || "N/A";
        const referenceNumber1 = referenceArray.find(ref => ref.code === "13")?.number || "N/A";
        const alternateTrackingJoined = tracking.data?.alternateTrackingNumber ? tracking.data.alternateTrackingNumber.map(a => a.number).join(', ') : "N/A";

        const deliveryDate = tracking.data?.deliveryDate?.[0]?.date;
        const formattedDeliveryDate = deliveryDate ? `${deliveryDate.slice(0, 4)}-${deliveryDate.slice(4, 6)}-${deliveryDate.slice(6, 8)}` : "N/A";
        const lastScanActivity = tracking.data?.activity?.[0] || {};
        const lastScanStatusText = lastScanActivity?.status?.statusCode === "011" ? "Delivered" : (lastScanActivity?.status?.description?.trim() || "No recent activity");
        const formattedLastScanDate = lastScanActivity.date ? `${lastScanActivity.date.slice(0, 4)}-${lastScanActivity.date.slice(4, 6)}-${lastScanActivity.date.slice(6, 8)}` : "N/A";
        const formattedLastScanTime = lastScanActivity.time ? `${lastScanActivity.time.slice(0, 2)}:${lastScanActivity.time.slice(2, 4)}:${lastScanActivity.time.slice(4, 6)}` : "N/A";
        const firstArrival = typeof findFirstScan === 'function' ? findFirstScan(tracking.data?.activity) : { text: 'No scan data' };

        return [
            "", tracking.number, referenceNumber1.slice(0, 6), firstArrival.text, lastScanStatusText, formattedDeliveryDate, lastScanStatusText,
            lastScanActivity.location?.address?.countryCode || "No recent activity", formattedLastScanTime, formattedLastScanDate,
            tracking.data?.deliveryInformation?.receivedBy || "N/A", tracking.data?.packageAddress?.[1]?.address?.countryCode || "N/A",
            tracking.data?.packageAddress?.[1]?.address?.city || "N/A", lastScanActivity.location?.slic || "N/A",
            tracking.data?.deliveryInformation?.location || "N/A", tracking.data?.service?.description || "N/A",
            tracking.data?.weight?.weight || "N/A", tracking.data?.packageAddress?.[0]?.address?.countryCode || "N/A",
            tracking.data?.packageAddress?.[0]?.address?.city || "N/A", tracking.data?.packageCount || "N/A",
            referenceNumber1, tracking.data?.dimension?.width || "N/A", tracking.data?.dimension?.height || "N/A",
            tracking.data?.dimension?.length || "N/A", ref1, ref2, ref3, ref4, ref5, alternateTrackingJoined, dimWeight ? dimWeight.toFixed(2) : "N/A"
        ];
    });

    const hotColumns = [
        { renderer: buttonRenderer, readOnly: true, disableVisualSelection: false, className: 'htNoSelection' },
        ...Array(hotHeaders.length - 1).fill({})
    ];

    return (
        <div className="tracking-view">
            <header className="tool-header">
                <button className="back-btn" onClick={onBack}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
                    Back to Dashboard
                </button>
                <div className="tool-title-section">
                    <span className="tool-icon">🔍</span>
                    <h1>Shipment Tracking</h1>
                </div>
            </header>

            <main className="tracking-content">
                <div className="control-panel">
                    <div className="input-section">
                        <textarea
                            rows="6"
                            placeholder="Enter tracking numbers (separated by commas or new lines)"
                            value={trackingNumbers}
                            onChange={(e) => setTrackingNumbers(e.target.value)}
                        />
                        <div className="action-buttons">
                            <input id="file-upload" type="file" accept=".txt,.xlsx,.xls" onChange={handleFileUpload} style={{ display: 'none' }} />
                            <label className="btn secondary" htmlFor="file-upload">Upload File</label>
                            <button className="btn primary" onClick={handleTrack} disabled={loading || trackingNumbers.trim() === ""}>
                                {loading ? "Tracking..." : "Track Shipments"}
                            </button>
                            {loading && <button className="btn danger" onClick={handleStop}>Stop</button>}
                            <button className="btn success" onClick={downloadExcel} disabled={trackingData.length === 0}>Download Excel</button>
                        </div>
                    </div>

                    {loading && (
                        <div className="progress-container">
                            <div className="progress-bar" style={{ width: `${progress}%` }}></div>
                            <span className="progress-text">{Math.round(progress)}% Complete</span>
                        </div>
                    )}

                    {error && <div className="error-message">{error}</div>}
                    {searchTime && <div className="info-message">Total search time: {searchTime} seconds</div>}
                </div>

                {trackingData.length > 0 && (
                    <div className="table-wrapper animated fadeIn">
                        <HotTable
                            ref={hotRef} data={hotData} width="100%" height="600px" rowHeaders={true} colHeaders={hotHeaders}
                            columns={hotColumns} columnSorting={true} dropdownMenu={true} filters={true} manualColumnResize={true}
                            manualRowResize={true} copyPaste={{ columnsLimit: 100, rowsLimit: 10000, pasteMode: 'overwrite' }}
                            fillHandle={true} selectionMode="multiple" contextMenu={true}
                            afterSelectionEnd={(r, c, r2, c2) => { if (hotRef.current?.hotInstance) hotRef.current.hotInstance.listen(); }}
                            licenseKey="non-commercial-and-evaluation"
                        />
                    </div>
                )}
            </main>
        </div>
    );
};

export default Tracking;
